/// <amd-module name="Test/Analytics/Widgets/PointHistoryWidget/PointHistoryWidgetSource" />

import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import { IODataQueryResponse } from "Analytics/Shared/Common/Types";
import { DateSKParser } from "Analytics/Shared/Utils/DateSKParser";

import { FilterHelper } from "Test/ManualTestReports/TestReportsCommon/FilterHelper";
import { PointHistoryFields } from "Test/Analytics/Widgets/PointHistoryWidget/PointHistoryWidgetConstants";
import { TestResourceEntityNames, TestPointHistorySnapshotFieldNames } from "Test/ManualTestReports/TestReportsCommon/Constants";
import { ITestProgressReportFilterState, IPointHistoryOutcomeTrendState, IFilterData } from "Test/ManualTestReports/TestReportsCommon/Contracts";
import { TestProgressReportSourceBase } from "Test/ManualTestReports/TestReportsCommon/TestProgressReportSourceBase";
import * as CommonResources from "Test/ManualTestReports/TestReportsCommon/Resources";
import { PointHistoryCommands } from "Test/Analytics/Widgets/PointHistoryWidget/PointHistoryWidgetConstants";

import * as Date_Utils from "VSSUI/Utilities/Date";

export class PointHistoryWidgetSource extends TestProgressReportSourceBase {

    public getPointHistoryWidgetProps(selectedCommand: PointHistoryCommands, filterData?: IFilterData, lastSelectedPlan?: number, fromDate?: Date, toDate?: Date): Promise<IPointHistoryOutcomeTrendState> {
        return this._getCurrentFilterState(filterData? filterData.filterState: undefined, lastSelectedPlan).then((currentFilterState: ITestProgressReportFilterState) => {
            return this._getTestPointHistoryGroupedByOutcomeAndDate(currentFilterState, selectedCommand, fromDate, toDate);

        });
    }

    private _getTestPointHistoryGroupedByOutcomeAndDate(currentFilterState: ITestProgressReportFilterState, selectedCommand: PointHistoryCommands, fromDate?: Date, toDate?: Date): Promise<IPointHistoryOutcomeTrendState> {
        if (selectedCommand == PointHistoryCommands.Last14Days) {
            currentFilterState.selectedDateRange = {
                fromDate: new Date(new Date().getTime() - 14*Date_Utils.day),
                toDate: new Date()
            };
        } else if (selectedCommand == PointHistoryCommands.Last30Days) {
            currentFilterState.selectedDateRange = {
                fromDate: new Date(new Date().getTime() - 30*Date_Utils.day),
                toDate: new Date()
            };
        } else {
            if (fromDate && toDate) {
                currentFilterState.selectedDateRange = {
                    fromDate: fromDate,
                    toDate: toDate
                };
            } else {
                currentFilterState.selectedDateRange = undefined;
            }
        }
        let filterStr = FilterHelper.getFilterString(currentFilterState, undefined, TestPointHistorySnapshotFieldNames.ResultOutcome);
        let groupByStr = this._getAggregateStrForPointHistory(TestPointHistorySnapshotFieldNames.DateSK);

        const queryOptions = {
            entityType: TestResourceEntityNames.PointHistorySnapshot,
            project: this._getProjectId()
        } as ODataQueryOptions;

        if (filterStr) {
            queryOptions.$apply = `${filterStr}/${groupByStr}`;
        }
        else {
            queryOptions.$apply = `${groupByStr}`;
        }

        return this.queryOData(queryOptions).then((oDataQueryResponse: IODataQueryResponse) => {
            return this._createPointHistoryOutcomeTrendState(oDataQueryResponse, currentFilterState);
        });
    }

    private _getAggregateStrForPointHistory(fieldName: string) {
        return `groupby((DateSK), aggregate($count as ${PointHistoryFields.TotalCount}, 
        cast(ResultOutcome  eq 'Passed', Edm.Int32) with sum as ${PointHistoryFields.Passed},
        cast(ResultOutcome  eq 'Failed', Edm.Int32) with sum as ${PointHistoryFields.Failed},
        cast(ResultOutcome eq 'Blocked', Edm.Int32) with sum as ${PointHistoryFields.Blocked},
        cast(ResultOutcome eq 'NotApplicable', Edm.Int32) with sum as ${PointHistoryFields.NotApplicable},
        cast(ResultOutcome eq 'None', Edm.Int32) with sum as ${PointHistoryFields.NotExecuted},
        cast(ResultOutcome ne 'None', Edm.Int32) with sum as ${PointHistoryFields.Executed}))`;
    }

    private _createPointHistoryOutcomeTrendState(odataQueryResponse: IODataQueryResponse, currentFilterState: ITestProgressReportFilterState): IPointHistoryOutcomeTrendState {
        var response: IPointHistoryOutcomeTrendState = {
            PassedCounts: [],
            FailedCounts: [],
            BlockedCounts: [],
            NotApplicableCounts: [],
            NotExecutedCounts: [],
            OtherCounts: [],
            TotalCounts: [],
            Dates: []
        } as IPointHistoryOutcomeTrendState;

        if (!odataQueryResponse || !odataQueryResponse.value) {
            throw new Error(CommonResources.OdataResponseEmpty);
        }

        var getCount = (row: any, columnName: any) => {
            return isNaN(row[columnName]) ? 0 : row[columnName];
        }
        let toDate = currentFilterState.selectedDateRange ? currentFilterState.selectedDateRange.toDate : new Date();
        let fromDate = currentFilterState.selectedDateRange ? currentFilterState.selectedDateRange.fromDate : new Date();
        let fromDateSK = DateSKParser.dateStringToDateSK(DateSKParser.convertDateToDateString(fromDate));
        var minDateSK: number = DateSKParser.dateStringToDateSK(DateSKParser.convertDateToDateString(toDate));

        odataQueryResponse.value.forEach((row: any) => {
                response.PassedCounts.push(getCount(row, PointHistoryFields.Passed));
                response.FailedCounts.push(getCount(row, PointHistoryFields.Failed));
                response.BlockedCounts.push(getCount(row, PointHistoryFields.Blocked));
                response.NotApplicableCounts.push(getCount(row, PointHistoryFields.NotApplicable));
                response.NotExecutedCounts.push(getCount(row, PointHistoryFields.NotExecuted));
                response.TotalCounts.push(getCount(row, PointHistoryFields.TotalCount));
                response.OtherCounts.push(getCount(row, PointHistoryFields.Executed) - 
                        (getCount(row, PointHistoryFields.Passed) + getCount(row, PointHistoryFields.Failed) + getCount(row, PointHistoryFields.Blocked)
                            + getCount(row, PointHistoryFields.NotApplicable)));
                let dateSK = row[PointHistoryFields.DateSK];
                response.Dates.push(dateSK);
                if (minDateSK > dateSK) {
                    minDateSK = dateSK;
                }
        });

        if (response.Dates.length > 0) {
            let counts: number[] = [];
            let dates: number[] = [];
            while (fromDateSK < minDateSK) {
                counts.push(0);
                dates.push(fromDateSK);

                fromDate = new Date(fromDate.getTime() + Date_Utils.day);
                fromDateSK = DateSKParser.dateStringToDateSK(DateSKParser.convertDateToDateString(fromDate));
            }
            
            response.PassedCounts.unshift(...counts);
            response.FailedCounts.unshift(...counts);
            response.BlockedCounts.unshift(...counts);
            response.NotApplicableCounts.unshift(...counts);
            response.NotExecutedCounts.unshift(...counts);
            response.TotalCounts.unshift(...counts);
            response.OtherCounts.unshift(...counts);
            response.Dates.unshift(...dates);
        }

        return response;
    }
}
